import { useState } from "react";
import {
  Inbox, Mail, RefreshCw, Loader2, Eye, AlertTriangle,
  ChevronDown, ChevronUp, CheckCircle2, XCircle, Trash2,
} from "lucide-react";
import { fmtDate, decisionTone } from "./utils";

export default function PipelinePanel({
  items, actioned, loading, error, sync,
  onRefresh, onUse, onDismiss, onQuickDecide, canApprove,
}) {
  const [showActioned, setShowActioned] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const lastSyncedLabel = sync?.last_fetched_at
    ? new Date(sync.last_fetched_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div className="bg-white border border-zinc-200/80 rounded-xl shadow-sm overflow-hidden flex flex-col h-full min-h-0" data-testid="ic-pipeline-panel">
      <header className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between bg-gradient-to-r from-brand-yellow/30 to-transparent">
        <div className="flex items-center gap-2 min-w-0">
          <Inbox className="w-4 h-4 text-zinc-700 flex-shrink-0" />
          <span className="font-display font-bold text-[14px] text-zinc-900">Property IC pipeline</span>
          <span className="text-[10px] font-bold bg-brand-ink text-white px-1.5 py-0.5 rounded">{items.length}</span>
          {sync?.cache_size > 0 && (
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" title={`Cached ${sync.cache_size} email(s)${lastSyncedLabel ? ' · last synced ' + lastSyncedLabel : ''}`} data-testid="ic-pipeline-sync-badge">
              {sync.mode === "incremental" ? "incremental" : "synced"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {actioned?.length > 0 && (
            <button onClick={() => setShowActioned(s => !s)} data-testid="ic-pipeline-toggle-actioned" className="text-[11px] text-zinc-600 hover:text-zinc-900 px-2 py-1 rounded-md hover:bg-zinc-100 inline-flex items-center gap-1">
              <Eye className="w-3 h-3" /> {showActioned ? "Hide" : "Show"} actioned ({actioned.length})
            </button>
          )}
          <button onClick={onRefresh} disabled={loading} data-testid="ic-pipeline-refresh" className="text-[11px] text-zinc-600 hover:text-zinc-900 px-2 py-1 rounded-md hover:bg-zinc-100 inline-flex items-center gap-1 disabled:opacity-50" title={lastSyncedLabel ? `Last synced ${lastSyncedLabel}` : "Sync inbox"}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-auto">
        {error && (
          <div className="m-3 p-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md flex gap-2" data-testid="ic-pipeline-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div><div className="font-bold">Inbox sync issue</div><div className="mt-1">{error}</div></div>
          </div>
        )}
        {!error && !loading && items.length === 0 && (
          <div className="p-8 text-center text-[12px] text-zinc-400" data-testid="ic-pipeline-empty">
            No new IC emails since 1 Aug 2025
          </div>
        )}
        {items.map(it => {
          const isOpen = expanded === it.id;
          return (
            <article key={it.id} className="border-b border-zinc-100" data-testid={`ic-pipeline-item-${it.id}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : it.id)}
                className="w-full text-left px-4 py-3 hover:bg-zinc-50/70 transition-colors"
                data-testid={`ic-pipeline-expand-${it.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-display font-bold text-[13.5px] text-zinc-900 truncate leading-snug">{it.property_guess}</h4>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate flex items-center gap-1.5">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{it.from?.split("<")[0].trim() || it.from}</span>
                      <span className="text-zinc-300">·</span>
                      <span className="flex-shrink-0">{fmtDate(it.date)}</span>
                    </div>
                    {!isOpen && <p className="text-[11.5px] text-zinc-600 mt-1.5 line-clamp-2">{it.snippet}</p>}
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" /> : <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />}
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-3 -mt-1" data-testid={`ic-pipeline-body-${it.id}`}>
                  <div className="bg-zinc-50/80 border border-zinc-200 rounded-md p-3 max-h-[220px] overflow-y-auto">
                    <div className="text-[11px] text-zinc-500 mb-1.5">{it.subject}</div>
                    <p className="text-[11.5px] text-zinc-800 whitespace-pre-line leading-relaxed">{it.body || it.snippet}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    <button onClick={(e) => { e.stopPropagation(); onUse(it); }} data-testid={`ic-pipeline-open-${it.id}`} className="text-[11px] font-medium bg-zinc-900 text-white px-2.5 py-1 rounded hover:bg-zinc-800">
                      Open IC →
                    </button>
                    {canApprove && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); onQuickDecide(it, "Approved"); }} data-testid={`ic-pipeline-approve-${it.id}`} className="text-[11px] font-medium bg-emerald-600 text-white px-2.5 py-1 rounded hover:bg-emerald-700 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Approve
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onQuickDecide(it, "Rejected"); }} data-testid={`ic-pipeline-reject-${it.id}`} className="text-[11px] font-medium bg-rose-600 text-white px-2.5 py-1 rounded hover:bg-rose-700 inline-flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); onDismiss(it); }} data-testid={`ic-pipeline-dismiss-${it.id}`} className="text-[11px] text-zinc-500 px-2 py-1 rounded hover:bg-zinc-100 inline-flex items-center gap-1 ml-auto" title="Hide from pipeline">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
        {showActioned && actioned.map(it => (
          <div key={"a-" + it.id} className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/50" data-testid={`ic-actioned-${it.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[12px] text-zinc-700 truncate">{it.property_guess}</div>
                <div className="text-[10px] text-zinc-400 mt-0.5">by {it.dismissed_by}</div>
              </div>
              <span className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded border ${decisionTone(it.dismissed_decision)}`}>{it.dismissed_decision}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
